import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { IUserProfile } from '../../models/login.model';
import { StorageService } from '../../services/storage/storage-service';
import { UserService } from '../../services/user/user.service';
import { UserProfileMenuComponent } from '../user-profile-menu/user-profile-menu.component';

@Component({
    selector: 'pims-profile-header',
    imports: [TranslatePipe, MatMenuModule, UserProfileMenuComponent],
    templateUrl: './profile-header.component.html',
    styleUrl: './profile-header.component.scss',
})
export class ProfileHeaderComponent implements OnInit {
    private readonly storageService = inject(StorageService);
    private readonly userService = inject(UserService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);

    userRoles: string[] = [];
    userProfile: IUserProfile | null = null;
    activeRole = '';
    hasImageError = false;

    ngOnInit() {
        this.userRoles = this.storageService.getLocalItem<string[]>(LOCAL_STORAGE_KEYS.USER_ROLES) ?? [];
        this.userProfile = this.storageService.getLocalItem<IUserProfile>(LOCAL_STORAGE_KEYS.USER_PROFILE);
        this.activeRole = this.formatRole(this.storageService.getLocalItem<string>(LOCAL_STORAGE_KEYS.ACTIVE_ROLE));

        this.userService.activeRole$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((role) => {
            if (role) {
                this.activeRole = this.formatRole(role);
                this.cdr.markForCheck();
            }
        });
    }

    onImageError(): void {
        this.hasImageError = true;
    }

    get profileImageUrl(): string | null {
        const img = this.userProfile?.profileImage?.trim();
        if (this.hasImageError || !img) return null;
        return img.startsWith('data:') || img.startsWith('http') ? img : `data:image/png;base64,${img}`;
    }

    get displayProfile(): string {
        const profile = this.userProfile?.profile?.trim();
        if (!profile) return '';
        return profile
            .split(' ')
            .map((w) =>
                w.length > 1 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
            )
            .join(' ');
    }

    get displayActiveRole(): string {
        const p = this.displayProfile.toLowerCase();
        const r = this.activeRole.trim();
        if (!r || p === r.toLowerCase()) return '';
        return p ? ` - ${r}` : r;
    }

    private formatRole(role?: string | null): string {
        if (!role) return '';
        return role
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }
}
